/**
 * InvoiceList Page - T096, T115, T116
 *
 * Invoice list with URL-persisted filters and bulk actions
 */
import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { Button, EmptyState, FilterPill, GlassPanel, LoadingSpinner, PageHeader, Pagination } from '../components/ui';
import { InvoiceCard } from '../components/invoices/InvoiceCard';
import { PaymentDateModal } from '../components/invoices/PaymentDateModal';
import {
  DEFAULT_INVOICE_COLUMN_ORDER,
  InvoiceListView,
} from '../components/invoices/InvoiceListView';
import { InvoiceFilters, type InvoiceFiltersValue } from '../components/invoices/InvoiceFilters';
import { useInvoiceList, useMarkInvoicePaid, useMarkInvoiceSent, useMarkInvoiceDeferred, useUnDeferInvoice, type InvoiceStatus } from '../hooks/useInvoices';
import { useInvoiceColumnPrefs } from '../hooks/useInvoiceColumnPrefs';
import { usePageSizePref } from '../hooks/usePageSizePref';
import { useInvoiceGroupByPref } from '../hooks/useInvoiceGroupByPref';
import { DateBasisControls } from '../components/shared/DateBasisControls';
import { PageSizePicker } from '../components/shared/PageSizePicker';
import { useToastHelpers } from '../components/ui/Toast';

// URL search params schema for type-safe URL state (T116)
const invoiceSearchSchema = z.object({
  dateBasis: z.enum(['invoiceDate', 'paymentDate']).optional().default('invoiceDate'),
  paymentStatus: z.enum(['all', 'known', 'unknown']).optional().default('all'),
  page: z.coerce.number().positive().optional().default(1),
  q: z.string().optional().default(''),
  // 'open' (Codex-006) = offene Forderungen = literal sent+overdue, so the
  // Dashboard "Offene Forderungen" drill-down reconciles with its card total.
  status: z.enum(['active', 'all', 'open', 'draft', 'sent', 'paid', 'overdue', 'cancelled', 'deferred']).optional().default('active'),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  sortBy: z
    .enum(['invoiceNumber', 'invoiceDate', 'dueDate', 'grossTotal', 'status', 'createdAt', 'paymentDate'])
    .optional()
    .default('invoiceDate'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
  view: z.enum(['list', 'grid']).optional(), // No default - resolved in component based on screen size
  // Sprint 006 A4 — tri-state: URL > localStorage > Default('quarter');
  // kein zod-default, damit 'kein Param' von 'explizit none' unterscheidbar ist.
  groupBy: z.enum(['none', 'quarter']).optional(),
  // T573 — page-size preference. URL wins over the localStorage cursor.
  limit: z.coerce.number().int().positive().optional(),
});

type InvoiceSearch = z.infer<typeof invoiceSearchSchema>;

export const Route = createFileRoute('/invoices')({
  component: InvoicesPage,
  validateSearch: invoiceSearchSchema,
});

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = React.useState(() => window.matchMedia('(min-width: 768px)').matches);
  React.useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

function InvoicesPage() {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const isDesktop = useIsDesktop();
  const toast = useToastHelpers();
  // Desktop defaults to list, mobile to grid
  const resolvedView = search.view ?? (isDesktop ? 'list' : 'grid');

  // Per-user column visibility/order for the list view (Phase 13 Cluster A).
  const [columnPrefs, setColumnPrefs] = useInvoiceColumnPrefs(DEFAULT_INVOICE_COLUMN_ORDER);

  // Selection state
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const markPaid = useMarkInvoicePaid();
  const markSent = useMarkInvoiceSent();
  const markDeferred = useMarkInvoiceDeferred();
  const unDefer = useUnDeferInvoice();
  const [bulkLoading, setBulkLoading] = React.useState(false);
  const [showBulkPaymentDateModal, setShowBulkPaymentDateModal] = React.useState(false);

  const today = new Date();
  const defaultPaymentDate = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, '0'),
    String(today.getDate()).padStart(2, '0'),
  ].join('-');

  // Build query from URL search params
  // 'active' is the default view — hides deferred invoices; 'all' shows every status including deferred.
  const activeStatuses: InvoiceStatus[] = ['draft', 'sent', 'paid', 'overdue', 'cancelled'];
  // T573: persisted page-size pref with URL-state override. The
  // grouping bump (quarter view) overrides both so a typical quarter
  // always fits on one page — but the user's cursor still drives
  // every other view.
  const { pageSize: userPageSize, setPageSize } = usePageSizePref(
    'invoice_list_page_size',
    search.limit
  );

  // Grouped totals describe the current page and selected filters.
  const { groupBy: wantGroupBy, setGroupBy } = useInvoiceGroupByPref(search.groupBy);
  const groupingSuppressed =
    wantGroupBy === 'quarter' && resolvedView !== 'list';
  const resolvedGroupBy: 'none' | 'quarter' =
    wantGroupBy === 'quarter' && !groupingSuppressed ? 'quarter' : 'none';

  const pageLimit = resolvedGroupBy === 'quarter' ? 200 : userPageSize;
  const query = {
    page: search.page,
    limit: pageLimit,
    q: search.q || undefined,
    status:
      search.status === 'active'
        ? activeStatuses
        : search.status === 'open'
          ? (['sent', 'overdue'] as InvoiceStatus[])
          : search.status === 'all' || search.status === 'overdue'
            ? undefined
            : (search.status as InvoiceStatus),
    overdue: search.status === 'overdue' ? true : undefined,
    dateBasis: search.dateBasis,
    paymentStatus: search.paymentStatus,
    dateFrom: search.dateFrom,
    dateTo: search.dateTo,
    sortBy: search.sortBy,
    sortOrder: search.sortOrder,
  };

  const { data, isLoading, error } = useInvoiceList(query);

  // Clear selection when data changes (filters, page)
  React.useEffect(() => {
    setSelectedIds(new Set());
  }, [search.page, search.q, search.status, search.dateFrom, search.dateTo, search.dateBasis, search.paymentStatus]);

  // Selection handlers
  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (!data?.invoices) return;
    if (selectedIds.size === data.invoices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(data.invoices.map((inv) => inv.id)));
    }
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  // Get selected invoices for bulk action validation
  const selectedInvoices = React.useMemo(() => {
    if (!data?.invoices) return [];
    return data.invoices.filter((inv) => selectedIds.has(inv.id));
  }, [data?.invoices, selectedIds]);

  // How many selected can transition to each status
  const canMarkPaid = selectedInvoices.filter(
    (inv) => ['draft', 'sent', 'overdue', 'deferred'].includes(inv.status)
  ).length;
  const canMarkSent = selectedInvoices.filter(
    (inv) => inv.status === 'draft'
  ).length;
  const canMarkDeferred = selectedInvoices.filter(
    (inv) => inv.status === 'sent' || inv.status === 'overdue'
  ).length;
  const canUnDefer = selectedInvoices.filter(
    (inv) => inv.status === 'deferred'
  ).length;

  const handleBulkMarkPaid = async (paymentDate: string) => {
    const eligible = selectedInvoices.filter(
      (inv) => ['draft', 'sent', 'overdue', 'deferred'].includes(inv.status)
    );
    if (eligible.length === 0) return;

    setBulkLoading(true);
    let success = 0;
    let failed = 0;

    await Promise.allSettled(
      eligible.map((inv) =>
        markPaid.mutateAsync({ id: inv.id, version: inv.version, paymentDate }).then(
          () => { success++; },
          () => { failed++; }
        )
      )
    );

    setBulkLoading(false);
    setShowBulkPaymentDateModal(false);
    setSelectedIds(new Set());
    if (failed > 0) {
      toast.error(`${success} als bezahlt markiert, ${failed} fehlgeschlagen`);
    } else {
      toast.success(`${success} Rechnung${success !== 1 ? 'en' : ''} als bezahlt markiert`);
    }
  };

  const handleBulkMarkSent = async () => {
    const eligible = selectedInvoices.filter((inv) => inv.status === 'draft');
    if (eligible.length === 0) return;

    setBulkLoading(true);
    let success = 0;
    let failed = 0;

    await Promise.allSettled(
      eligible.map((inv) =>
        markSent.mutateAsync({ id: inv.id, version: inv.version }).then(
          () => { success++; },
          () => { failed++; }
        )
      )
    );

    setBulkLoading(false);
    setSelectedIds(new Set());
    if (failed > 0) {
      toast.error(`${success} als gesendet markiert, ${failed} fehlgeschlagen`);
    } else {
      toast.success(`${success} Rechnung${success !== 1 ? 'en' : ''} als gesendet markiert`);
    }
  };

  const handleBulkMarkDeferred = async () => {
    const eligible = selectedInvoices.filter(
      (inv) => inv.status === 'sent' || inv.status === 'overdue'
    );
    if (eligible.length === 0) return;

    setBulkLoading(true);
    let success = 0;
    let failed = 0;

    await Promise.allSettled(
      eligible.map((inv) =>
        markDeferred.mutateAsync({ id: inv.id, version: inv.version }).then(
          () => { success++; },
          () => { failed++; }
        )
      )
    );

    setBulkLoading(false);
    setSelectedIds(new Set());
    if (failed > 0) {
      toast.error(`${success} zurückgestellt, ${failed} fehlgeschlagen`);
    } else {
      toast.success(`${success} Rechnung${success !== 1 ? 'en' : ''} zurückgestellt`);
    }
  };

  const handleBulkUnDefer = async () => {
    const eligible = selectedInvoices.filter((inv) => inv.status === 'deferred');
    if (eligible.length === 0) return;

    setBulkLoading(true);
    let success = 0;
    let failed = 0;

    await Promise.allSettled(
      eligible.map((inv) =>
        unDefer.mutateAsync({ id: inv.id, version: inv.version }).then(
          () => { success++; },
          () => { failed++; }
        )
      )
    );

    setBulkLoading(false);
    setSelectedIds(new Set());
    if (failed > 0) {
      toast.error(`${success} reaktiviert, ${failed} fehlgeschlagen`);
    } else {
      toast.success(`${success} Rechnung${success !== 1 ? 'en' : ''} reaktiviert`);
    }
  };

  // Update URL when filters change (T116)
  const handleFiltersChange = (filters: InvoiceFiltersValue) => {
    navigate({
      search: (prev: InvoiceSearch) => ({
        ...prev,
        page: 1, // Reset to first page on filter change
        q: filters.q || undefined,
        status: filters.status,
        paymentStatus: (filters.dateFrom || filters.dateTo) && prev.paymentStatus === 'unknown' ? 'known' : prev.paymentStatus,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
      }),
      replace: true,
    });
  };

  const handlePageChange = (page: number) => {
    navigate({
      search: (prev: InvoiceSearch) => ({ ...prev, page }),
      replace: true,
    });
  };

  const handleSortChange = (sortBy: InvoiceSearch['sortBy'], sortOrder: InvoiceSearch['sortOrder']) => {
    navigate({
      search: (prev: InvoiceSearch) => ({ ...prev, sortBy, sortOrder, page: 1 }),
      replace: true,
    });
  };

  // Current filter values for the filter component
  const filterValue: InvoiceFiltersValue = {
    q: search.q || '',
    status: search.status || 'all',
    dateFrom: search.dateFrom,
    dateTo: search.dateTo,
  };

  return (
    <ProtectedRoute>
      <AppShell>
        {/* Header — Sprint 006 A1: shared PageHeader (title/subtitle/actions
            + controls-row als children), gleiche Anatomie wie alle Listen. */}
        <PageHeader
          title="Rechnungen"
          subtitle={`${data?.total ?? 0} Rechnungen`}
          actions={
            <Link to="/invoices/new">
              <Button>
                <svg className="w-4 h-4 lg:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="hidden lg:inline">Neue Rechnung</span>
              </Button>
            </Link>
          }
        >
            {/* View toggle */}
            <div className="flex items-center bg-white/5 border border-white/10 rounded-lg h-8">
              <button
                onClick={() => navigate({ search: (prev) => ({ ...prev, view: 'list' }), replace: true })}
                className={`h-full px-2.5 rounded-l-md transition-colors ${
                  resolvedView === 'list'
                    ? 'bg-white/10 text-white'
                    : 'text-white/40 hover:text-white/70'
                }`}
                title="Liste"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <button
                onClick={() => navigate({ search: (prev) => ({ ...prev, view: 'grid' }), replace: true })}
                className={`h-full px-2.5 rounded-r-md transition-colors ${
                  resolvedView === 'grid'
                    ? 'bg-white/10 text-white'
                    : 'text-white/40 hover:text-white/70'
                }`}
                title="Kacheln"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
            </div>

            {/* Sort dropdown - only visible in grid view */}
            {resolvedView === 'grid' && (
              <select
                value={`${search.sortBy}-${search.sortOrder}`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = e.target.value.split('-') as [
                    InvoiceSearch['sortBy'],
                    InvoiceSearch['sortOrder']
                  ];
                  handleSortChange(sortBy, sortOrder);
                }}
                className="bg-white/5 border border-white/10 rounded-lg px-3 h-8 text-sm text-white/70 focus:outline-none focus:ring-2 focus:ring-white/20 flex-1 lg:flex-none"
              >
                <option value="invoiceDate-desc">Datum (neueste)</option>
                <option value="invoiceDate-asc">Datum (älteste)</option>
                <option value="invoiceNumber-desc">Nr. (absteigend)</option>
                <option value="invoiceNumber-asc">Nr. (aufsteigend)</option>
                <option value="grossTotal-desc">Betrag (höchste)</option>
                <option value="grossTotal-asc">Betrag (niedrigste)</option>
                <option value="dueDate-asc">Fälligkeit (nächste)</option>
                <option value="dueDate-desc">Fälligkeit (späteste)</option>
              </select>
            )}
        </PageHeader>

        {/* Bulk action bar */}
        <AnimatePresence initial={false}>
        {selectedIds.size > 0 && (
          <motion.div
            key="bulk-bar"
            initial={{ opacity: 0, y: -8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
          <div className="mb-4 flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
            <span className="text-sm text-white/70">
              {selectedIds.size} ausgewählt
            </span>
            <div className="h-4 w-px bg-white/10" />
            {canMarkPaid > 0 && (
              <button
                onClick={() => setShowBulkPaymentDateModal(true)}
                disabled={bulkLoading}
                className="text-sm text-green-400 hover:text-green-300 transition-colors disabled:opacity-50"
              >
                Als bezahlt markieren ({canMarkPaid})
              </button>
            )}
            {canMarkSent > 0 && (
              <button
                onClick={handleBulkMarkSent}
                disabled={bulkLoading}
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors disabled:opacity-50"
              >
                Als gesendet markieren ({canMarkSent})
              </button>
            )}
            {canMarkDeferred > 0 && (
              <button
                onClick={handleBulkMarkDeferred}
                disabled={bulkLoading}
                className="text-sm text-orange-400 hover:text-orange-300 transition-colors disabled:opacity-50"
              >
                Zurückstellen ({canMarkDeferred})
              </button>
            )}
            {canUnDefer > 0 && (
              <button
                onClick={handleBulkUnDefer}
                disabled={bulkLoading}
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors disabled:opacity-50"
              >
                Reaktivieren ({canUnDefer})
              </button>
            )}
            <div className="flex-1" />
            <button
              onClick={handleClearSelection}
              className="text-sm text-white/40 hover:text-white/70 transition-colors"
            >
              Auswahl aufheben
            </button>
          </div>
          </motion.div>
        )}
        </AnimatePresence>

        <PaymentDateModal
          isOpen={showBulkPaymentDateModal}
          initialDate={defaultPaymentDate}
          title={`${canMarkPaid} Rechnung${canMarkPaid === 1 ? '' : 'en'} als bezahlt markieren`}
          confirmLabel="Als bezahlt markieren"
          isPending={bulkLoading}
          onClose={() => setShowBulkPaymentDateModal(false)}
          onConfirm={handleBulkMarkPaid}
        />

        {/* Filters (T111, T115) — 006b P3: Quartale-Toggle (T566) wandert
            als trailing-Slot IN die Filter-Fläche (eine Fläche, eine
            Pill-Sprache; FilterPill wurde seinerzeit aus genau diesem
            Toggle extrahiert). Testid + aria-pressed unverändert. */}
        <InvoiceFilters
          value={filterValue}
          onChange={handleFiltersChange}
          trailing={
            <>
                <DateBasisControls dateBasis={search.dateBasis} paymentStatus={search.paymentStatus}
                  onBasisChange={(basis) => navigate({ search: (prev) => ({ ...prev, dateBasis: basis, paymentStatus: 'all', page: 1 }), replace: true })}
                  onPaymentStatusChange={(status) => navigate({ search: (prev) => ({ ...prev, paymentStatus: status, dateFrom: undefined, dateTo: undefined, page: 1 }), replace: true })} />
                <span className="text-xs text-white/50">Filter und Quartale folgen der Datumsbasis. Unbekannte Zahlungstage gehören zu keinem Zahlungsquartal.</span>
                {resolvedView === 'list' && (
                <FilterPill
                  active={resolvedGroupBy === 'quarter'}
                  data-testid="invoice-quartale-toggle"
                  title="Nach Quartal gruppieren"
                  className={groupingSuppressed ? 'opacity-60' : ''}
                  icon={
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  }
                  onClick={() => {
                    const next = wantGroupBy === 'quarter' ? 'none' : 'quarter';
                    setGroupBy(next);
                    navigate({
                      search: (prev: InvoiceSearch) => ({ ...prev, groupBy: next, page: 1 }),
                      replace: true,
                    });
                  }}
                >
                  Nach Quartalen gruppieren
                </FilterPill>
                )}
              </>
          }
        />

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-400 mb-4">Fehler beim Laden der Rechnungen</p>
            <Button variant="secondary" onClick={() => window.location.reload()}>
              Erneut versuchen
            </Button>
          </div>
        ) : data?.invoices.length === 0 ? (
          <EmptyState
            title={search.q || (search.status !== 'all' && search.status !== 'active') || search.dateFrom ? 'Keine Treffer' : 'Keine Rechnungen'}
            description={
              search.q || (search.status !== 'all' && search.status !== 'active') || search.dateFrom
                ? 'Passen Sie die Filter an oder erstellen Sie eine neue Rechnung'
                : 'Erstellen Sie Ihre erste Rechnung'
            }
            action={{
              label: 'Neue Rechnung',
              onClick: () => navigate({ to: '/invoices/new' }),
            }}
          />
        ) : (
          <>
            <GlassPanel
              variant="subtle"
              padding={resolvedView === 'list' ? 'none' : 'sm'}
              className="overflow-hidden"
              data-testid="invoices-list-surface"
            >
              {resolvedView === 'list' ? (
                <InvoiceListView
                  invoices={data?.invoices ?? []}
                  sortBy={search.sortBy}
                  sortOrder={search.sortOrder}
                  onSortChange={handleSortChange}
                  selectedIds={selectedIds}
                  onToggleSelect={handleToggleSelect}
                  onSelectAll={handleSelectAll}
                  columnPrefs={columnPrefs}
                  onColumnPrefsChange={setColumnPrefs}
                  groupBy={resolvedGroupBy}
                  dateBasis={search.dateBasis}
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data?.invoices.map((invoice) => (
                    <InvoiceCard
                      key={invoice.id}
                      invoice={invoice}
                      selected={selectedIds.has(invoice.id)}
                      onToggleSelect={handleToggleSelect}
                    />
                  ))}
                </div>
              )}
            </GlassPanel>

            {data && data.totalPages > 1 && (
              <div className="flex flex-col sm:flex-row sm:justify-center sm:items-center gap-3 mt-6">
                <Pagination
                  page={search.page ?? 1}
                  totalPages={data.totalPages}
                  onPageChange={handlePageChange}
                />
                {/* T573 — Rechnungen pro Seite. Hidden in quarter-grouping
                    view (page-size is bumped to 200 by the grouping logic
                    above, so a manual picker would be misleading). */}
                {resolvedGroupBy !== 'quarter' && (
                  <PageSizePicker
                    value={userPageSize}
                    onChange={(next) => {
                      setPageSize(next);
                      navigate({
                        search: (prev: InvoiceSearch) => ({
                          ...prev,
                          limit: next,
                          page: 1,
                        }),
                        replace: true,
                      });
                    }}
                  />
                )}
              </div>
            )}
          </>
        )}
      </AppShell>
    </ProtectedRoute>
  );
}
